import React, { useState, useEffect } from 'react';
import '../Css/PaymentSettings.css';
import '@tabler/icons-webfont/dist/tabler-icons.min.css';
import Navbar from './Navbar';
import { Link, useSearchParams } from 'react-router-dom';

const ACTION_META = {
    'bill.add': { ic: 'ti-plus', cls: 'ps-a-add', verb: 'added party' },
    'bill.remove': { ic: 'ti-minus', cls: 'ps-a-remove', verb: 'removed party' },
    'settings.update': { ic: 'ti-settings', cls: 'ps-a-settings', verb: 'updated settings' },
    'access.grant': { ic: 'ti-user-plus', cls: 'ps-a-access', verb: 'granted access to' },
    'access.revoke': { ic: 'ti-user-minus', cls: 'ps-a-access', verb: 'revoked access from' },
    'campaign.create': { ic: 'ti-flag', cls: 'ps-a-settings', verb: 'created campaign' },
    'payment.received': { ic: 'ti-currency-rupee', cls: 'ps-a-add', verb: 'received payment' }
};

const PaymentSettings = () => {
    const API = process.env.REACT_APP_API || window.location.origin;
    const adminEmail = sessionStorage.getItem('userEmail') || '';
    const [searchParams] = useSearchParams();
    const initialTab = searchParams.get('tab') || 'settings';

    const [activeTab, setActiveTab] = useState(initialTab);
    const [toasts, setToasts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [access, setAccess] = useState([]);
    const [candidates, setCandidates] = useState([]);
    const [history, setHistory] = useState([]);
    const [audit, setAudit] = useState([]);

    const [settingsForm, setSettingsForm] = useState({
        defaultChannels: [],
        throttlePerDay: 1,
        throttlePerWeek: 3,
        escalateSalespersonAfter: 3,
        escalateManagerAfter: 7,
        escalateOwnerAfterDays: 15,
        senderName: '',
        supportNumber: '',
        paymentBaseUrl: 'pay.zordigo.com'
    });

    const [historyFilters, setHistoryFilters] = useState({ customer: '', channel: '', status: '' });
    const [auditAction, setAuditAction] = useState('');
    const [selectedUser, setSelectedUser] = useState('');

    const showToast = (msg, kind = 'ok') => {
        const id = Date.now();
        setToasts((prev) => [...prev, { id, msg, kind }]);
        setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 2600);
    };

    const fmtTime = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) + ' ' +
            d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    };

    const apiCall = async (method, path, body) => {
        const separator = path.includes('?') ? '&' : '?';
        const url = adminEmail
            ? `${API}${path}${separator}adminEmail=${encodeURIComponent(adminEmail)}`
            : `${API}${path}`;
        const options = { method, headers: { 'Content-Type': 'application/json' } };
        if (body !== undefined) options.body = JSON.stringify(body);
        const response = await fetch(url, options);
        if (!response.ok) {
            let error = { error: response.statusText };
            try { error = await response.json(); } catch (e) { /* ignore */ }
            throw new Error(error.error || `HTTP ${response.status}`);
        }
        return response.status === 204 ? null : response.json();
    };

    const loadSettings = async () => {
        setLoading(true);
        try {
            const data = await apiCall('GET', '/api/reminder-settings');
            if (data) {
                setSettingsForm({
                    defaultChannels: data.defaultChannels || [],
                    throttlePerDay: data.throttlePerDay ?? 1,
                    throttlePerWeek: data.throttlePerWeek ?? 3,
                    escalateSalespersonAfter: data.escalateSalespersonAfter ?? 3,
                    escalateManagerAfter: data.escalateManagerAfter ?? 7,
                    escalateOwnerAfterDays: data.escalateOwnerAfterDays ?? 15,
                    senderName: data.senderName || '',
                    supportNumber: data.supportNumber || '',
                    paymentBaseUrl: data.paymentBaseUrl || 'pay.zordigo.com'
                });
            }
        } catch (error) {
            showToast(error.message, 'bad');
        } finally {
            setLoading(false);
        }
    };

    const saveSettings = async () => {
        try {
            await apiCall('PUT', '/api/reminder-settings', settingsForm);
            showToast('Settings saved');
        } catch (error) {
            showToast(error.message, 'bad');
        }
    };

    const loadAccess = async () => {
        setLoading(true);
        try {
            const [accessData, candidatesData] = await Promise.all([
                apiCall('GET', '/api/reminder-access'),
                apiCall('GET', '/api/reminder-access/candidates')
            ]);
            setAccess(accessData || []);
            setCandidates(candidatesData || []);
            if (candidatesData?.length > 0) setSelectedUser(candidatesData[0].id);
        } catch (error) {
            showToast(error.message, 'bad');
        } finally {
            setLoading(false);
        }
    };

    const togglePermission = async (userId, permKey) => {
        const user = access.find((a) => a.userId === userId);
        if (!user || user.role === 'owner' || user.role === 'admin') return;
        const updatedUser = { ...user, [permKey]: !user[permKey] };
        try {
            await apiCall('POST', '/api/reminder-access', {
                userId,
                canAccess: updatedUser.canAccess,
                canAddParties: updatedUser.canAddParties,
                canRemoveParties: updatedUser.canRemoveParties,
                canManageSettings: updatedUser.canManageSettings,
                canGrantAccess: updatedUser.canGrantAccess || false
            });
            setAccess((prev) => prev.map((a) => (a.userId === userId ? updatedUser : a)));
            showToast('Permission updated');
        } catch (error) {
            showToast(error.message, 'bad');
        }
    };

    const grantAccess = async () => {
        if (!selectedUser) return;
        try {
            await apiCall('POST', '/api/reminder-access', {
                userId: selectedUser,
                canAccess: true,
                canAddParties: true,
                canRemoveParties: false,
                canManageSettings: false,
                canGrantAccess: false
            });
            showToast('Access granted');
            await loadAccess();
        } catch (error) {
            showToast(error.message, 'bad');
        }
    };

    const revokeAccess = async (userId, name) => {
        if (!window.confirm(`Revoke ${name}'s access to the reminder panel?`)) return;
        try {
            await apiCall('DELETE', '/api/reminder-access?userId=' + encodeURIComponent(userId));
            showToast('Access revoked');
            await loadAccess();
        } catch (error) {
            showToast(error.message, 'bad');
        }
    };

    const loadHistory = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (historyFilters.customer) params.set('customer', historyFilters.customer);
            if (historyFilters.channel) params.set('channel', historyFilters.channel);
            if (historyFilters.status) params.set('status', historyFilters.status);
            const qs = params.toString();
            const data = await apiCall('GET', '/api/reminder-history' + (qs ? '?' + qs : ''));
            setHistory(data || []);
        } catch (error) {
            showToast(error.message, 'bad');
        } finally {
            setLoading(false);
        }
    };

    const loadAudit = async () => {
        setLoading(true);
        try {
            const path = auditAction ? `/api/reminder-audit?action=${auditAction}` : '/api/reminder-audit';
            const data = await apiCall('GET', path);
            setAudit(data || []);
        } catch (error) {
            showToast(error.message, 'bad');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'settings') loadSettings();
        else if (activeTab === 'access') loadAccess();
        else if (activeTab === 'history') loadHistory();
        else if (activeTab === 'audit') loadAudit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const toggleDefaultChannel = (ch) => {
        setSettingsForm((prev) => {
            const next = prev.defaultChannels.includes(ch)
                ? prev.defaultChannels.filter((c) => c !== ch)
                : [...prev.defaultChannels, ch];
            return { ...prev, defaultChannels: next };
        });
    };

    const channelIcon = (ch) => {
        if (ch === 'whatsapp') return 'ti-brand-whatsapp';
        if (ch === 'email') return 'ti-mail';
        return 'ti-message';
    };

    const auditDetail = (d) => {
        if (d.paymentId) {
            return (
                <>
                    {' '}
                    <b>{d.customer || d.invoice || 'payment'}</b>
                    {d.invoice ? ` · ${d.invoice}` : ''}
                    {' · '}
                    <span style={{ fontFamily: 'var(--mono, monospace)', fontSize: 11 }}>{d.paymentId}</span>
                    {d.amount != null ? ` · ₹${Math.round(d.amount).toLocaleString('en-IN')}` : ''}
                </>
            );
        }
        if (d.customer) return <> <b>{d.customer}</b>{d.invoice ? ' · ' + d.invoice : ''}</>;
        if (d.user) return <> <b>{d.user}</b></>;
        if (d.throttlePerDay != null) return <> (throttle {d.throttlePerDay}/day)</>;
        return null;
    };

    return (
        <div className="ps-page">
            <Navbar />

            <div className="ps-page-actions">
                <Link to="/PaymentReminders" className="ps-page-btn"><i className="ti ti-arrow-left"></i> Back to reminders</Link>
            </div>

            <div className="ps-wrap">
                <div className="ps-tabs">
                    {[
                        { id: 'settings', label: 'Settings', icon: 'ti-settings' },
                        { id: 'access', label: 'Access & Permissions', icon: 'ti-users' },
                        { id: 'history', label: 'Reminder History', icon: 'ti-history' },
                        { id: 'audit', label: 'User Activity', icon: 'ti-list-check' }
                    ].map((t) => (
                        <button key={t.id} type="button" className={`ps-tab ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>
                            <i className={`ti ${t.icon}`}></i> {t.label}
                        </button>
                    ))}
                </div>

                {activeTab === 'settings' && (
                    <div className="ps-panel">
                        <div className="ps-panel-head">
                            <div className="ps-panel-title"><i className="ti ti-adjustments"></i> Defaults &amp; limits</div>
                            <div className="ps-panel-sub">Applied to new reminder campaigns.</div>
                        </div>
                        <div className="ps-panel-body">
                            {loading ? <div className="ps-spinner"></div> : (
                                <>
                                    <div className="ps-row">
                                        <div><div className="ps-row-label">Default channels</div><div className="ps-row-sub">Pre-selected when creating a campaign</div></div>
                                        <div className="ps-channels">
                                            {['whatsapp', 'email', 'sms'].map((ch) => (
                                                <button key={ch} type="button" className={`ps-ch-pill ${settingsForm.defaultChannels.includes(ch) ? 'on' : ''}`} onClick={() => toggleDefaultChannel(ch)}>
                                                    <i className={`ti ${channelIcon(ch)}`}></i> {ch === 'whatsapp' ? 'WhatsApp' : ch.charAt(0).toUpperCase() + ch.slice(1)}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="ps-row">
                                        <div><div className="ps-row-label">Smart throttle</div><div className="ps-row-sub">Don&apos;t over-message a customer</div></div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--ink-2)' }}>
                                            max <input className="ps-num-input" type="number" value={settingsForm.throttlePerDay} onChange={(e) => setSettingsForm({ ...settingsForm, throttlePerDay: +e.target.value })} /> /day ·
                                            <input className="ps-num-input" type="number" value={settingsForm.throttlePerWeek} onChange={(e) => setSettingsForm({ ...settingsForm, throttlePerWeek: +e.target.value })} /> /week
                                        </div>
                                    </div>
                                    <div className="ps-row">
                                        <div><div className="ps-row-label">Escalation ladder</div><div className="ps-row-sub">Auto-notify up the chain when ignored</div></div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--ink-2)', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                            salesperson @<input className="ps-num-input" type="number" value={settingsForm.escalateSalespersonAfter} onChange={(e) => setSettingsForm({ ...settingsForm, escalateSalespersonAfter: +e.target.value })} /> ·
                                            manager @<input className="ps-num-input" type="number" value={settingsForm.escalateManagerAfter} onChange={(e) => setSettingsForm({ ...settingsForm, escalateManagerAfter: +e.target.value })} /> ·
                                            owner @<input className="ps-num-input" type="number" value={settingsForm.escalateOwnerAfterDays} onChange={(e) => setSettingsForm({ ...settingsForm, escalateOwnerAfterDays: +e.target.value })} />d
                                        </div>
                                    </div>
                                    <div className="ps-row">
                                        <div><div className="ps-row-label">Sender name</div><div className="ps-row-sub">Shown as the business in messages</div></div>
                                        <input className="ps-text-input" value={settingsForm.senderName} onChange={(e) => setSettingsForm({ ...settingsForm, senderName: e.target.value })} />
                                    </div>
                                    <div className="ps-row">
                                        <div><div className="ps-row-label">Support number</div></div>
                                        <input className="ps-text-input" value={settingsForm.supportNumber} onChange={(e) => setSettingsForm({ ...settingsForm, supportNumber: e.target.value })} />
                                    </div>
                                    <div className="ps-row">
                                        <div><div className="ps-row-label">Payment link domain</div><div className="ps-row-sub">Base URL for generated pay links</div></div>
                                        <input className="ps-text-input" value={settingsForm.paymentBaseUrl} onChange={(e) => setSettingsForm({ ...settingsForm, paymentBaseUrl: e.target.value })} />
                                    </div>
                                    <div className="ps-save-bar">
                                        <button type="button" className="ps-btn primary" onClick={saveSettings}><i className="ti ti-device-floppy"></i> Save settings</button>
                                    </div>
                                    <div className="ps-perm-note">
                                        <i className="ti ti-info-circle"></i>
                                        <div>Only users with <b>Manage settings</b> permission can save changes here. Every change is recorded in the User Activity log.</div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'access' && (
                    <div className="ps-panel">
                        <div className="ps-panel-head">
                            <div className="ps-panel-title"><i className="ti ti-shield-lock"></i> Who can use the reminder panel</div>
                            <div className="ps-panel-sub">Control who may open the panel, and who can add or remove parties (bills).</div>
                        </div>
                        <div className="ps-panel-body">
                            {loading ? <div className="ps-spinner"></div> : (
                                <>
                                    <div className="ps-acc-row ps-acc-head">
                                        <div>User</div>
                                        <div style={{ textAlign: 'center' }}>Access</div>
                                        <div style={{ textAlign: 'center' }}>Add</div>
                                        <div style={{ textAlign: 'center' }}>Remove</div>
                                        <div style={{ textAlign: 'center' }}>Settings</div>
                                        <div></div>
                                    </div>
                                    {access.map((a) => {
                                        const isOwner = a.role === 'owner' || a.role === 'admin';
                                        const perm = (key, on) => (
                                            <div
                                                className={`ps-perm-check ${on ? 'on' : ''} ${isOwner ? 'locked' : ''}`}
                                                onClick={isOwner ? undefined : () => togglePermission(a.userId, key)}
                                            ></div>
                                        );
                                        return (
                                            <div key={a.userId} className="ps-acc-row">
                                                <div>
                                                    <div className="ps-acc-name">
                                                        {a.name} {isOwner && <span className="ps-owner-badge">owner</span>}
                                                    </div>
                                                    <div className="ps-acc-role">{a.role}{a.grantedBy ? ' · by ' + a.grantedBy : ''}</div>
                                                </div>
                                                <div>{perm('canAccess', a.canAccess)}</div>
                                                <div>{perm('canAddParties', a.canAddParties)}</div>
                                                <div>{perm('canRemoveParties', a.canRemoveParties)}</div>
                                                <div>{perm('canManageSettings', a.canManageSettings)}</div>
                                                <div style={{ textAlign: 'right' }}>
                                                    {!isOwner && (
                                                        <button type="button" className="ps-revoke-icon" title="Revoke access" onClick={() => revokeAccess(a.userId, a.name)}>
                                                            <i className="ti ti-trash"></i>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {candidates.length > 0 && (
                                        <div className="ps-grant-row">
                                            <div className="ps-grant-label">Add a user to the panel</div>
                                            <div className="ps-grant-flex">
                                                <select className="ps-sel" value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)}>
                                                    {candidates.map((c) => (
                                                        <option key={c.id} value={c.id}>{c.name} ({c.role})</option>
                                                    ))}
                                                </select>
                                                <button type="button" className="ps-btn ghost sm" onClick={grantAccess}><i className="ti ti-user-plus"></i> Grant access</button>
                                            </div>
                                        </div>
                                    )}
                                    <div className="ps-perm-note">
                                        <i className="ti ti-shield-check"></i>
                                        <div><b>Add</b> lets a user attach bills/parties to a campaign. <b>Remove</b> lets them take parties off. Owners always have every permission. Each grant, change, and removal is logged in User Activity.</div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'history' && (
                    <div className="ps-panel">
                        <div className="ps-panel-head">
                            <div className="ps-panel-title"><i className="ti ti-message-2-share"></i> Reminder history</div>
                            <div className="ps-panel-sub">Reminder sends and payment receipts, including Payment IDs for verification.</div>
                        </div>
                        <div className="ps-panel-body">
                            <div className="ps-filters">
                                <input className="ps-text-input" style={{ width: 'auto', flex: 1, minWidth: 140 }} placeholder="Filter by customer…" value={historyFilters.customer} onChange={(e) => setHistoryFilters({ ...historyFilters, customer: e.target.value })} />
                                <select className="ps-sel" value={historyFilters.channel} onChange={(e) => setHistoryFilters({ ...historyFilters, channel: e.target.value })}>
                                    <option value="">All channels</option>
                                    <option value="whatsapp">WhatsApp</option>
                                    <option value="email">Email</option>
                                    <option value="sms">SMS</option>
                                    <option value="payment">Payment received</option>
                                </select>
                                <select className="ps-sel" value={historyFilters.status} onChange={(e) => setHistoryFilters({ ...historyFilters, status: e.target.value })}>
                                    <option value="">All statuses</option>
                                    <option value="delivered">Delivered</option>
                                    <option value="sent">Sent</option>
                                    <option value="queued">Queued</option>
                                    <option value="failed">Failed</option>
                                    <option value="clicked">Clicked</option>
                                    <option value="payment_received">Payment received</option>
                                </select>
                                <button type="button" className="ps-btn primary sm" onClick={loadHistory}>Filter</button>
                            </div>
                            {loading ? <div className="ps-spinner"></div> : history.length === 0 ? (
                                <div className="ps-empty"><i className="ti ti-message-off"></i>No reminders sent yet matching these filters.</div>
                            ) : history.map((s, i) => (
                                <div key={i} className="ps-hist-row">
                                    <span className={`ps-rk ${s.recipientKind || 'retailer'}`}>{s.channel === 'payment' ? 'pay' : s.recipientKind}</span>
                                    <div style={{ minWidth: 0 }}>
                                        <div className="truncate" style={{ fontWeight: 500 }}>{s.customer} <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>· {s.invoice}</span></div>
                                        <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>
                                            {s.channel === 'payment' || s.status === 'payment_received' ? (
                                                <>
                                                    <i className="ti ti-currency-rupee" style={{ fontSize: 10 }}></i>
                                                    {' '}Payment ID <span style={{ fontFamily: 'monospace' }}>{s.paymentId || s.to}</span>
                                                    {s.amount != null ? ` · ₹${Math.round(s.amount).toLocaleString('en-IN')}` : ''}
                                                    {s.utr ? ` · UTR ${s.utr}` : ''}
                                                </>
                                            ) : (
                                                <>
                                                    <i className={`ti ${channelIcon(s.channel)}`} style={{ fontSize: 10 }}></i> {s.channel} → {s.to || ''}{s.includedPod ? ' · 📎 signed bill' : ''}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <span className={`ps-st ${s.status}`}>{s.status === 'payment_received' ? 'paid' : s.status}</span>
                                    <span style={{ fontSize: 10, color: 'var(--ink-3)' }}>{fmtTime(s.deliveredAt || s.sentAt || s.queuedAt)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === 'audit' && (
                    <div className="ps-panel">
                        <div className="ps-panel-head">
                            <div className="ps-panel-title"><i className="ti ti-user-check"></i> User activity log</div>
                            <div className="ps-panel-sub">History of user names — who added/removed parties, changed settings, or granted access.</div>
                        </div>
                        <div className="ps-panel-body">
                            <div className="ps-filters">
                                <select className="ps-sel" value={auditAction} onChange={(e) => setAuditAction(e.target.value)}>
                                    <option value="">All activity</option>
                                    <option value="bill.add">Party added</option>
                                    <option value="bill.remove">Party removed</option>
                                    <option value="settings.update">Settings changed</option>
                                    <option value="access.grant">Access granted</option>
                                    <option value="access.revoke">Access revoked</option>
                                    <option value="campaign.create">Campaign created</option>
                                    <option value="payment.received">Payment received</option>
                                </select>
                                <button type="button" className="ps-btn primary sm" onClick={loadAudit}>Filter</button>
                            </div>
                            {loading ? <div className="ps-spinner"></div> : audit.length === 0 ? (
                                <div className="ps-empty"><i className="ti ti-history-off"></i>No activity recorded yet.</div>
                            ) : audit.map((r, i) => {
                                const m = ACTION_META[r.action] || { ic: 'ti-point', cls: 'ps-a-settings', verb: r.action };
                                return (
                                    <div key={i} className="ps-audit-row">
                                        <div className={`ps-audit-dot ${m.cls}`}><i className={`ti ${m.ic}`}></i></div>
                                        <div className="ps-audit-main">
                                            <div className="ps-audit-text">
                                                <span className="ps-audit-actor">{r.actor}</span> {m.verb}{auditDetail(r.detail)}
                                            </div>
                                        </div>
                                        <div className="ps-audit-time">{fmtTime(r.at)}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            <div className="ps-toast-wrap">
                {toasts.map((t) => (
                    <div key={t.id} className={`ps-toast ${t.kind === 'bad' ? 'bad' : ''}`}>
                        <i className={`ti ${t.kind === 'bad' ? 'ti-alert-triangle' : 'ti-check'}`}></i> {t.msg}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default PaymentSettings;
